import { supabase, describeError } from "@/lib/supabase";

/**
 * Generic CRUD adapter on top of a Supabase table.
 * Assumes each table has columns: id (uuid), user_id (uuid), and timestamps.
 *
 * options:
 *  - table: Supabase table name
 *  - orderBy: column for default sort (default: "created_at")
 *  - ascending: sort direction (default: false)
 *  - filterColumn: optional column for client-side filtering (e.g. "profile_id" or "profile_name")
 */
export function makeAdapter({ table, orderBy = "created_at", ascending = false, filterColumn = "profile_id" }) {
  return {
    async list({ filterValue } = {}) {
      let q = supabase.from(table).select("*").order(orderBy, { ascending });
      if (filterValue && filterValue !== "all") {
        q = q.eq(filterColumn, filterValue);
      }
      const { data, error } = await q;
      if (error) throw new Error(describeError(error));
      return data || [];
    },
    async create(payload) {
      const { data: userData } = await supabase.auth.getUser();
      const user_id = userData?.user?.id;
      if (!user_id) throw new Error("Not signed in");
      const row = { ...payload, user_id };
      const { data, error } = await supabase.from(table).insert(row).select().single();
      if (error) throw new Error(describeError(error));
      return data;
    },
    async update(id, payload) {
      const { data, error } = await supabase
        .from(table)
        .update(payload)
        .eq("id", id)
        .select()
        .single();
      if (error) throw new Error(describeError(error));
      return data;
    },
    async remove(id) {
      const { error } = await supabase.from(table).delete().eq("id", id);
      if (error) throw new Error(describeError(error));
      return true;
    },
  };
}
